export const THEME_STORAGE_KEY = "bs.prefers-color-scheme";
export const THEME_DARK = "dark";
export const THEME_LIGHT = "light";
export const THEME_CHANGE_EVENT = "gl-theme-change";

export type AppTheme = typeof THEME_DARK | typeof THEME_LIGHT;

/**
 * Reads the saved theme preference from localStorage.
 */
export function getStoredTheme(): AppTheme | null {
	try {
		const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
		if (storedTheme === THEME_DARK || storedTheme === THEME_LIGHT) {
			return storedTheme;
		}
	} catch (error) {
		// Ignore storage access errors (private browsing, etc.).
	}

	return null;
}

/**
 * Returns the theme currently applied on the document root.
 */
export function getAppliedTheme(): AppTheme {
	if (document.documentElement.classList.contains(THEME_LIGHT)) {
		return THEME_LIGHT;
	}

	return THEME_DARK;
}

/**
 * Applies a theme to the document and optionally persists it.
 */
export function applyTheme(theme: AppTheme, persist: boolean = true): void {
	const rootElement = document.documentElement;
	rootElement.classList.remove(THEME_DARK, THEME_LIGHT);
	rootElement.classList.add(theme);
	rootElement.setAttribute("data-bs-theme", theme);

	if (persist) {
		try {
			localStorage.setItem(THEME_STORAGE_KEY, theme);
		} catch (error) {
			// Ignore storage access errors (private browsing, etc.).
		}
	}

	window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: theme }));
}

/**
 * Toggles between dark and light themes and returns the newly applied theme.
 */
export function toggleTheme(): AppTheme {
	const nextTheme: AppTheme = getAppliedTheme() === THEME_DARK ? THEME_LIGHT : THEME_DARK;
	applyTheme(nextTheme, true);
	return nextTheme;
}

/**
 * Initializes theme from storage, defaulting to dark when unset.
 */
export function initializeTheme(): AppTheme {
	const storedTheme = getStoredTheme();
	const theme: AppTheme = storedTheme ?? THEME_DARK;
	applyTheme(theme, false);
	return theme;
}

export type ChartThemeColors = {
	axis: string;
	grid: string;
	tooltipBackground: string;
	tooltipBorder: string;
	tooltipText: string;
};

/**
 * Returns Recharts axis/grid/tooltip colors for the applied app theme.
 */
export function getChartThemeColors(): ChartThemeColors {
	if (getAppliedTheme() === THEME_LIGHT) {
		return {
			axis: "#212529",
			grid: "#dbe7f7",
			tooltipBackground: "#ffffff",
			tooltipBorder: "#dee2e6",
			tooltipText: "#212529",
		};
	}

	return {
		axis: "#ffffff",
		grid: "#333333",
		tooltipBackground: "#2c3e50",
		tooltipBorder: "#34495e",
		tooltipText: "#ffffff",
	};
}

export type ChartSeriesColors = {
	primary: string;
	primaryFillStart: string;
	projected: string;
	goal: string;
};

/**
 * Returns primary chart series colors for the applied app theme.
 */
export function getChartSeriesColors(): ChartSeriesColors {
	if (getAppliedTheme() === THEME_LIGHT) {
		return {
			primary: "#228be6",
			primaryFillStart: "#228be6",
			projected: "#1864ab",
			goal: "#40c057",
		};
	}

	return {
		primary: "#3498db",
		primaryFillStart: "#3498db",
		projected: "#3498db",
		goal: "#2ecc71",
	};
}
